import React, { useCallback, useEffect, useState } from "react";
import {
  fetchGenerations,
  deleteGeneration,
  getGenerationDownloadUrl,
  fetchGenerationStatus,
} from "../services/api";
import { supabase } from "../lib";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Download,
  Trash2,
  RefreshCw,
  Video,
  Wand2,
  Sparkles,
} from "lucide-react";

import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../components/ui";

const formatDate = (value) => {
  if (!value) {
    return "Unknown";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const statusBadgeStyles = {
  completed: "bg-emerald-100 text-emerald-700",
  processing: "bg-amber-100 text-amber-700",
  failed: "bg-rose-100 text-rose-700",
};

const MyVideos = () => {
  const { user } = useAuth();
  const [generations, setGenerations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState(null);

  const userId = user?.id;

  const loadGenerations = useCallback(async ({ silent = false } = {}) => {
    if (!userId) {
      setGenerations([]);
      return;
    }
    if (!silent) {
      setLoading(true);
      setError("");
    }
    try {
      const data = await fetchGenerations();
      const enriched =
        data?.generations?.map((generation) => ({
          ...generation,
          theme: generation.theme ?? generation.theme_id ?? "",
          previewRetryCount: 0,
          previewRefreshing: false,
          previewError: null,
        })) || [];
      setGenerations(enriched);
    } catch (err) {
      console.error("[frontend] Failed to load generations", err);
      if (!silent) {
        const message =
          err?.response?.data?.detail ||
          err.message ||
          "Unable to load videos.";
        setError(message);
        toast.error(message);
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [userId]);

  useEffect(() => {
    loadGenerations();
  }, [loadGenerations]);

  useEffect(() => {
    if (!userId) return;

    const channel = supabase
      .channel("realtime-my-videos")
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "user_generations",
          filter: `user_id=eq.${userId}`,
        },
        async (payload) => {
          if (payload.new.status === "completed") {
            try {
              // Fetch fresh data including the signed URL
              const data = await fetchGenerationStatus(payload.new.job_id);

              setGenerations((prev) =>
                prev.map((gen) => {
                  if (gen.id === payload.new.id) {
                    return {
                      ...gen,
                      status: "completed",
                      download_url: data.video_url,
                      // Update other fields if needed
                      video_object_name: payload.new.video_object_name,
                    };
                  }
                  return gen;
                })
              );
              toast.success("A video just finished processing! 🎥");
            } catch (err) {
              console.error("Failed to update completed video:", err);
            }
          } else if (payload.new.status === "failed") {
            setGenerations((prev) =>
              prev.map((gen) => {
                if (gen.id === payload.new.id) {
                  return {
                    ...gen,
                    status: "failed",
                  };
                }
                return gen;
              })
            );
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId]);

  const handleDownload = async (generationId) => {
    if (!userId) return;

    try {
      const data = await getGenerationDownloadUrl(generationId);
      if (data?.download_url) {

        window.open(data.download_url, "_blank", "noopener,noreferrer");
      } else {
        toast.error("Download link unavailable right now.");
      }
    } catch (err) {
      console.error("[frontend] Failed to download generation", err);
      const message =
        err?.response?.data?.detail ||
        err.message ||
        "Unable to start download.";
      setError(message);
      toast.error(message);
    }
  };

  const confirmDelete = async () => {
    if (!userId || !videoToDelete) return;
    try {
      await deleteGeneration(videoToDelete.id);
      setGenerations((prev) =>
        prev.filter((item) => item.id !== videoToDelete.id)
      );

    } catch (err) {
      console.error("[frontend] Failed to delete generation", err);
      const message =
        err?.response?.data?.detail ||
        err.message ||
        "Unable to delete video.";
      setError(message);
      toast.error(message);
    } finally {
      setVideoToDelete(null);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await loadGenerations({ silent: true });

    } finally {
      setRefreshing(false);
    }
  };


  const handlePreviewError = async (generationId) => {
    if (!userId) {
      return;
    }
    setGenerations((prev) =>
      prev.map((generation) => {
        if (generation.id !== generationId) {
          return generation;
        }
        const retryCount = generation.previewRetryCount || 0;
        if (retryCount >= 1) {
          return {
            ...generation,
            previewError: "Preview unavailable. Please refresh later.",
            previewRefreshing: false,
          };
        }
        return {
          ...generation,
          previewRetryCount: retryCount + 1,
          previewRefreshing: true,
          previewError: null,
        };
      })
    );

    try {
      const data = await getGenerationDownloadUrl(generationId);
      setGenerations((prev) =>
        prev.map((generation) => {
          if (generation.id !== generationId) {
            return generation;
          }
          return {
            ...generation,
            download_url: data?.download_url || generation.download_url,
            previewRefreshing: false,
            previewError: null,
          };
        })
      );

    } catch (err) {
      console.error("[frontend] Failed to refresh preview URL", err);
      const message =
        err?.response?.data?.detail ||
        err?.message ||
        "Unable to refresh preview link.";
      setGenerations((prev) =>
        prev.map((generation) => {
          if (generation.id !== generationId) {
            return generation;
          }
          return {
            ...generation,
            previewRefreshing: false,
            previewError: message,
          };
        })
      );
      toast.error(message);
    }
  };

  return (
    <div className="space-y-10">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-semibold text-foreground">
            My Magic Videos
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Browse, download, or delete the animated stories you&apos;ve made.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={handleRefresh}
          disabled={loading || refreshing}
          className="w-full rounded-full px-5 py-2 text-sm font-semibold sm:w-auto"
        >
          <RefreshCw
            className={`mr-2 h-4 w-4 ${loading || refreshing ? "animate-spin" : ""
              }`}
          />
          {refreshing || loading ? "Refreshing…" : "Refresh"}
        </Button>
      </header>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, index) => (
            <div
              key={index}
              className="h-64 animate-pulse rounded-3xl border border-border/40 bg-muted/40"
            />
          ))}
        </div>
      ) : generations.length === 0 ? (
        <Card className="border border-dashed border-border/60 bg-muted/30 text-center shadow-none">
          <CardContent className="flex flex-col items-center justify-center gap-4 py-16">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Video className="h-8 w-8" />
            </div>
            <CardTitle className="text-2xl font-semibold text-foreground">
              No magic videos yet
            </CardTitle>
            <CardDescription className="max-w-md text-base text-muted-foreground">
              Head to the Magic Studio and bring your first doodle to life!
            </CardDescription>
            <Button asChild className="rounded-full px-6 py-3 text-sm font-semibold">
              <a href="/dashboard">
                <Wand2 className="mr-2 h-4 w-4" />
                Open Magic Studio
              </a>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {generations.map((generation) => (
            <Card
              key={generation.id}
              className="relative flex h-full flex-col overflow-hidden border border-border/60 bg-white/85 shadow-card"
            >
              <div className="relative aspect-video bg-muted/40">
                {generation.download_url ? (
                  <video
                    src={generation.download_url}
                    controls
                    muted
                    playsInline
                    preload="metadata"
                    className="h-full w-full object-cover"
                    onError={() => handlePreviewError(generation.id)}
                  />
                ) : (
                  <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-muted-foreground">
                    <Video className="h-10 w-10" />
                    <span className="text-xs">Preview not available</span>
                  </div>
                )}
                <div className="absolute left-3 top-3">
                  <Badge
                    className={`rounded-full px-3 py-1 text-xs font-semibold uppercase ${statusBadgeStyles[generation.status] ||
                      "bg-muted text-muted-foreground"
                      }`}
                  >
                    {generation.status === "completed"
                      ? "Ready!"
                      : generation.status}
                  </Badge>
                </div>
                {generation.previewRefreshing && (
                  <div className="absolute inset-0 flex items-center justify-center bg-background/80 text-xs font-semibold text-muted-foreground">
                    Refreshing preview…
                  </div>
                )}
              </div>

              <CardHeader className="space-y-1">
                <CardTitle className="flex items-center justify-between text-lg">
                  <span className="capitalize">
                    {generation.theme?.replace("_", " ") || "Untitled video"}
                  </span>
                  <span className="text-sm font-semibold text-muted-foreground">
                    {generation.duration_seconds}s
                  </span>
                </CardTitle>
                <CardDescription className="text-xs">
                  {formatDate(generation.created_at)}
                </CardDescription>
              </CardHeader>
              <CardContent className="flex-1">
                {generation.previewError && (
                  <Alert variant="destructive" className="mt-4">
                    <AlertDescription className="text-xs">
                      {generation.previewError}
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
              <CardFooter className="flex items-center justify-between gap-3">
                <Button
                  variant="secondary"
                  className="w-full"
                  onClick={() => handleDownload(generation.id)}
                  disabled={generation.status !== "completed"}
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download
                </Button>
                <AlertDialog
                  open={videoToDelete?.id === generation.id}
                  onOpenChange={(open) =>
                    setVideoToDelete(open ? generation : null)
                  }
                >
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full text-destructive"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        Delete this magic video?
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        This can’t be undone. You’ll need to reanimate the
                        doodle if you change your mind.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Keep this video</AlertDialogCancel>
                      <AlertDialogAction onClick={confirmDelete}>
                        Yes, delete it
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </CardFooter>
            </Card>
          ))}
        </div>
      )}

      <div className="rounded-3xl border border-border/60 bg-muted/30 px-6 py-5 text-sm text-muted-foreground">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4" />
            <span>
              Tip: refresh the page if a preview looks stale—new videos appear
              within a few seconds after generation.
            </span>
          </div>
          <a
            href="/dashboard"
            className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-primary/80"
          >
            <Wand2 className="h-4 w-4" />
            Create another video
          </a>
        </div>
      </div>
    </div>
  );
};

export default MyVideos;

